import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_FEATURE_VISIBILITY,
  isPageVisibleForUser,
  PAGE_FEATURE_MAP,
} from '../constants/featureVisibility';
import { fetchFeatureVisibility } from '../lib/supabase/featureVisibilitySync';
import { useIsAdmin } from '../hooks/useIsAdmin';

/** @typedef {import('../constants/featureVisibility').FeatureKey} FeatureKey */
/** @typedef {import('../constants/featureVisibility').FeatureVisibilityMap} FeatureVisibilityMap */

/** @type {React.Context<{ visibility: FeatureVisibilityMap; loading: boolean; refresh: () => Promise<void>; isFeatureVisible: (key: FeatureKey) => boolean; isPageVisible: (pageId: string) => boolean } | null>} */
const FeatureVisibilityContext = createContext(null);

export function FeatureVisibilityProvider({ children }) {
  const { isAdmin } = useIsAdmin();
  const [visibility, setVisibility] = useState(() => ({ ...DEFAULT_FEATURE_VISIBILITY }));
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const next = await fetchFeatureVisibility();
      setVisibility(next);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const isFeatureVisible = useCallback(
    (key) => {
      if (isAdmin) return true;
      return visibility[key] ?? DEFAULT_FEATURE_VISIBILITY[key];
    },
    [isAdmin, visibility],
  );

  const isPageVisible = useCallback(
    (pageId) => isPageVisibleForUser(pageId, visibility, isAdmin),
    [isAdmin, visibility],
  );

  const value = useMemo(
    () => ({ visibility, loading, refresh, isFeatureVisible, isPageVisible }),
    [visibility, loading, refresh, isFeatureVisible, isPageVisible],
  );

  return <FeatureVisibilityContext.Provider value={value}>{children}</FeatureVisibilityContext.Provider>;
}

export function useFeatureVisibility() {
  const ctx = useContext(FeatureVisibilityContext);
  if (!ctx) {
    return {
      visibility: { ...DEFAULT_FEATURE_VISIBILITY },
      loading: false,
      refresh: async () => {},
      isFeatureVisible: (key) => DEFAULT_FEATURE_VISIBILITY[key] ?? true,
      isPageVisible: (pageId) => {
        const feature = PAGE_FEATURE_MAP[pageId];
        if (!feature) return true;
        return DEFAULT_FEATURE_VISIBILITY[feature];
      },
    };
  }
  return ctx;
}
